import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { qualityAPI, platformSettingsAPI, fetchAllPages } from '../../../lib/api';
import type { SourceOfRejection } from '../../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import ApprovalsManagementSection from './ApprovalsManagementSection';
import WeekStartSection from './WeekStartSection';
import { ConcessionFeatureSection } from './ConcessionFeatureSection';
import CostOfQualitySection from './CostOfQualitySection';
import FaultTaxonomySection from './FaultTaxonomySection';
import FaultCodeMappingSection from './FaultCodeMappingSection';

export function QualityTab() {
  const [loading, setLoading] = useState(true);

  // NCR Leadtime settings
  const [containmentLeadtime, setContainmentLeadtime] = useState<number>(2);
  const [correctiveLeadtime, setCorrectiveLeadtime] = useState<number>(28);
  const [initialContainment, setInitialContainment] = useState<number>(2);
  const [initialCorrective, setInitialCorrective] = useState<number>(28);

  // Approval Expiry Alert settings
  const [approvalExpiryDays, setApprovalExpiryDays] = useState<number>(15);
  const [initialApprovalExpiry, setInitialApprovalExpiry] = useState<number>(15);

  // CR-5534: Approval Register expiry-warning threshold. Drives the
  // matrix amber band — separate from approvalExpiryDays which gates
  // the email-alert window.
  const [registerWarningDays, setRegisterWarningDays] = useState<number>(90);
  const [initialRegisterWarning, setInitialRegisterWarning] = useState<number>(90);

  // NCR Email Notification settings
  const [customerDepartment, setCustomerDepartment] = useState<string>('quality');
  const [supplierDepartment, setSupplierDepartment] = useState<string>('quality');
  const [emailFrequency, setEmailFrequency] = useState<'weekly' | 'weekdays'>('weekdays');
  const [emailWeekdays, setEmailWeekdays] = useState<number[]>([]);

  // CR-7293 / CR-7320: organisation being configured (Helix picks one when there are several)
  const [organisation, setOrganisation] = useState<string | undefined>(undefined);
  // Bumped when the fault taxonomy changes, so the mapping reloads its category choices.
  const [taxonomyVersion, setTaxonomyVersion] = useState(0);

  // Department options
  const departmentOptions = [
    { value: 'quality', label: 'Quality Department Users' },
    { value: 'purchasing', label: 'Procurement Department Users' },
    { value: 'engineering', label: 'Engineering Department Users' },
    { value: 'production', label: 'Production Department Users' },
    { value: 'management', label: 'Management Users' },
    { value: 'all', label: 'All Users' },
  ];

  const weekdayOptions = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  // Sources of rejection
  const [sources, setSources] = useState<SourceOfRejection[]>([]);
  const [newSourceName, setNewSourceName] = useState('');
  const [addingSource, setAddingSource] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      const [allSources, settingsResp] = await Promise.all([
        fetchAllPages<SourceOfRejection>((page) => qualityAPI.getSourcesOfRejection({ page_size: 100, page })),
        platformSettingsAPI.getSettings(),
      ]);
      setSources(allSources);
      // Load NCR leadtime and approval expiry settings
      if (settingsResp.data) {
        const containment = settingsResp.data.containment_action_leadtime ?? 2;
        const corrective = settingsResp.data.full_corrective_action_leadtime ?? 28;
        const approvalExpiry = settingsResp.data.approval_expiry_alert_days ?? 15;
        const registerWarning = settingsResp.data.accreditation_expiry_warning_days ?? 90;
        setContainmentLeadtime(containment);
        setCorrectiveLeadtime(corrective);
        setInitialContainment(containment);
        setInitialCorrective(corrective);
        setApprovalExpiryDays(approvalExpiry);
        setInitialApprovalExpiry(approvalExpiry);
        setRegisterWarningDays(registerWarning);
        setInitialRegisterWarning(registerWarning);
        // NCR Email Notification settings
        setCustomerDepartment(settingsResp.data.ncr_customer_department ?? 'quality');
        setSupplierDepartment(settingsResp.data.ncr_supplier_department ?? 'quality');
        setEmailFrequency(settingsResp.data.ncr_email_frequency ?? 'weekdays');
        setEmailWeekdays(settingsResp.data.ncr_email_weekdays ?? []);
      }
    } catch (error) {
      console.error('Error loading quality config:', error);
      toast.error('Failed to load quality configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleContainmentBlur = async () => {
    if (containmentLeadtime === initialContainment) return;
    try {
      await platformSettingsAPI.patchSettings({
        containment_action_leadtime: containmentLeadtime,
      });
      setInitialContainment(containmentLeadtime);
      toast.success('Containment action leadtime saved');
    } catch (error) {
      console.error('Error saving containment leadtime:', error);
      toast.error('Failed to save containment leadtime');
      setContainmentLeadtime(initialContainment);
    }
  };

  const handleCorrectiveBlur = async () => {
    if (correctiveLeadtime === initialCorrective) return;
    try {
      await platformSettingsAPI.patchSettings({
        full_corrective_action_leadtime: correctiveLeadtime,
      });
      setInitialCorrective(correctiveLeadtime);
      toast.success('Corrective action leadtime saved');
    } catch (error) {
      console.error('Error saving corrective leadtime:', error);
      toast.error('Failed to save corrective leadtime');
      setCorrectiveLeadtime(initialCorrective);
    }
  };

  const handleApprovalExpiryBlur = async () => {
    if (approvalExpiryDays === initialApprovalExpiry) return;
    try {
      await platformSettingsAPI.patchSettings({
        approval_expiry_alert_days: approvalExpiryDays,
      });
      setInitialApprovalExpiry(approvalExpiryDays);
      toast.success('Approval expiry alert saved');
    } catch (error) {
      console.error('Error saving approval expiry alert:', error);
      toast.error('Failed to save approval expiry alert');
      setApprovalExpiryDays(initialApprovalExpiry);
    }
  };

  // CR-5534: persist the Approval Register expiry-warning threshold.
  const handleRegisterWarningBlur = async () => {
    if (registerWarningDays === initialRegisterWarning) return;
    try {
      await platformSettingsAPI.patchSettings({
        accreditation_expiry_warning_days: registerWarningDays,
      });
      setInitialRegisterWarning(registerWarningDays);
      toast.success('Approval Register threshold saved');
    } catch (error) {
      console.error('Error saving register warning days:', error);
      toast.error('Failed to save threshold');
      setRegisterWarningDays(initialRegisterWarning);
    }
  };

  // NCR Email Notification handlers
  const handleCustomerDepartmentChange = async (value: string) => {
    setCustomerDepartment(value);
    try {
      await platformSettingsAPI.patchSettings({ ncr_customer_department: value });
      toast.success('Customer department saved');
    } catch (error) {
      console.error('Error saving customer department:', error);
      toast.error('Failed to save customer department');
    }
  };

  const handleSupplierDepartmentChange = async (value: string) => {
    setSupplierDepartment(value);
    try {
      await platformSettingsAPI.patchSettings({ ncr_supplier_department: value });
      toast.success('Supplier department saved');
    } catch (error) {
      console.error('Error saving supplier department:', error);
      toast.error('Failed to save supplier department');
    }
  };

  const handleEmailFrequencyChange = async (value: 'weekly' | 'weekdays') => {
    setEmailFrequency(value);
    try {
      await platformSettingsAPI.patchSettings({ ncr_email_frequency: value });
      toast.success('Email frequency saved');
    } catch (error) {
      console.error('Error saving email frequency:', error);
      toast.error('Failed to save email frequency');
    }
  };

  const handleWeekdayToggle = async (day: number) => {
    const newWeekdays = emailWeekdays.includes(day)
      ? emailWeekdays.filter(d => d !== day)
      : [...emailWeekdays, day].sort((a, b) => a - b);
    setEmailWeekdays(newWeekdays);
    try {
      await platformSettingsAPI.patchSettings({ ncr_email_weekdays: newWeekdays });
      toast.success('Weekday selection saved');
    } catch (error) {
      console.error('Error saving weekday selection:', error);
      toast.error('Failed to save weekday selection');
    }
  };

  // Source of Rejection CRUD
  const handleAddSource = async () => {
    const name = newSourceName.trim();
    if (!name) return;
    try {
      setAddingSource(true);
      const resp = await qualityAPI.createSourceOfRejection({ name });
      setSources(prev => [...prev, resp.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewSourceName('');
      toast.success(`Added "${name}"`);
    } catch (error) {
      console.error('Error adding source:', error);
      toast.error(getApiErrorMessage(error, 'Failed to add source of rejection'));
    } finally {
      setAddingSource(false);
    }
  };

  const handleDeleteSource = async (id: number, name: string) => {
    try {
      await qualityAPI.deleteSourceOfRejection(id);
      setSources(prev => prev.filter(s => s.id !== id));
      toast.success(`Removed "${name}"`);
    } catch {
      toast.error('Cannot delete - may be in use by existing NCRs');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* CR-6385: master switch for the concession workflow. First on the tab
          because it decides whether several of the settings below apply at all. */}
      <ConcessionFeatureSection />
      {/* NCR Leadtime and Approval Expiry Alert - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* NCR Leadtime Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold uppercase tracking-wide">NCR Leadtime</CardTitle>
          </CardHeader>
            <CardContent className="flex items-center justify-between">
            <div className="space-y-2">
              <Label htmlFor="containment-leadtime" className="text-sm text-muted-foreground">
                Containment Action Leadtime
              </Label>
              <Input
                id="containment-leadtime"
                type="number"
                min={1}
                max={365}
                value={containmentLeadtime}
                onChange={(e) => setContainmentLeadtime(parseInt(e.target.value) || 1)}
                onBlur={handleContainmentBlur}
                className="max-w-[150px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="corrective-leadtime" className="text-sm text-muted-foreground">
                Full Corrective Action Leadtime (days)
              </Label>
              <Input
                id="corrective-leadtime"
                type="number"
                min={1}
                max={365}
                value={correctiveLeadtime}
                onChange={(e) => setCorrectiveLeadtime(parseInt(e.target.value) || 1)}
                onBlur={handleCorrectiveBlur}
                className="max-w-[150px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Approval Expiry Alert + Register threshold */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold uppercase tracking-wide">Approval Expiry Alert</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="approval-expiry" className="text-sm text-muted-foreground">
                Approval Expiry Alert
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="approval-expiry"
                  type="number"
                  min={1}
                  max={365}
                  value={approvalExpiryDays}
                  onChange={(e) => setApprovalExpiryDays(parseInt(e.target.value) || 1)}
                  onBlur={handleApprovalExpiryBlur}
                  className="max-w-[150px]"
                />
                <span className="text-sm text-muted-foreground">Days Prior</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Lead time for the email notification sent before an accreditation expires.
              </p>
            </div>

            {/* CR-5534: Register matrix "expiring soon" threshold */}
            <div className="space-y-2 pt-2 border-t">
              <Label htmlFor="register-warning" className="text-sm text-muted-foreground">
                Approval Register Warning Threshold
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="register-warning"
                  type="number"
                  min={1}
                  max={365}
                  value={registerWarningDays}
                  onChange={(e) => setRegisterWarningDays(parseInt(e.target.value) || 1)}
                  onBlur={handleRegisterWarningBlur}
                  className="max-w-[150px]"
                />
                <span className="text-sm text-muted-foreground">Days Prior</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Accreditations expiring within this many days are flagged as
                expiring soon on the Quality → Approval Register matrix.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* NCR Email Notification and Email Notification - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* NCR Email Notification */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold uppercase tracking-wide">NCR Email Notification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="customer-department" className="text-sm text-muted-foreground">
                Customer
              </Label>
              <select
                id="customer-department"
                value={customerDepartment}
                onChange={(e) => handleCustomerDepartmentChange(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {departmentOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier-department" className="text-sm text-muted-foreground">
                Supplier
              </Label>
              <select
                id="supplier-department"
                value={supplierDepartment}
                onChange={(e) => handleSupplierDepartmentChange(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {departmentOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Email Notification */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold uppercase tracking-wide">Email Notification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email-frequency" className="text-sm text-muted-foreground">
                NCR - Email Frequency
              </Label>
              <div className="flex items-start gap-4">
                <select
                  id="email-frequency"
                  value={emailFrequency}
                  onChange={(e) => handleEmailFrequencyChange(e.target.value as 'weekly' | 'weekdays')}
                  className="flex h-10 w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="weekly">Weekly</option>
                  <option value="weekdays">Weekdays</option>
                </select>
                {emailFrequency === 'weekdays' && (
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {weekdayOptions.map(day => (
                      <label key={day.value} className="flex items-center gap-1.5 text-sm">
                        <input
                          type="checkbox"
                          checked={emailWeekdays.includes(day.value)}
                          onChange={() => handleWeekdayToggle(day.value)}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        {day.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly OTIF week-start boundary */}
      <WeekStartSection />

      {/* CR-7293: Cost of Quality rate — group or site level */}
      <CostOfQualitySection organisation={organisation} onOrganisationChange={setOrganisation} />

      <p className="text-sm text-muted-foreground">
        Configure the dropdown options available when creating NCRs. These are mandatory fields on NCR creation.
      </p>

      {/* Sources of Rejection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sources of Rejection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {sources.map(source => (
            <div key={source.id} className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded-md">
              <span className="text-sm">{source.name}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => handleDeleteSource(source.id, source.name)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          {sources.length === 0 && (
            <p className="text-sm text-muted-foreground py-2">No rejection sources yet. Add at least one so quality users can tag where each NCR was found.</p>
          )}
          <div className="flex gap-2 pt-2">
            <Input
              value={newSourceName}
              onChange={(e) => setNewSourceName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddSource(); }}
              placeholder="New source name..."
              className="flex-1"
            />
            <Button onClick={handleAddSource} disabled={addingSource || !newSourceName.trim()} size="sm">
              {addingSource ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Fault Groups & Categories — the taxonomy the ERP mapping below points into */}
      <FaultTaxonomySection onChanged={() => setTaxonomyVersion((v) => v + 1)} />

      {/* CR-7320: ERP fault-code -> fault category mapping (customer admins / Helix) */}
      <FaultCodeMappingSection key={`mapping-${taxonomyVersion}`} organisation={organisation} />

      {/* CR-5333: Approvals Management — supplier accreditation requirements */}
      <ApprovalsManagementSection />
    </div>
  );
}
